import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ContextActionType, useDumbActionsContext } from '@europrocurement/flexy-components';
import { ColumnDatatable, FiltersDatatableList } from '@europrocurement/flexy-datatable';
import { OffresJsonldOffreRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import {
    FaOptionIcon,
    addItem,
    visibilityFullIcon,
    visibilityPartialIcon,
} from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/sharp-regular-svg-icons';
import { visibilityTranslation } from '@europrocurement/l2d-utils';
import { useOffreService } from '@europrocurement/l2d-domain';
import { Typography } from '@mui/material';
import RoutedListing from '../components/RoutedListing';
import { displayCustomValue } from '../listings';
import useFilterOptions from '../Catalog/useFilterOptions';
import { getStaticSelectFilter } from '../components/filters';
import { PossibleTypes } from '../../Societes';
import { offerCreatePath, offerFichePath } from './routes';

const Panel: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const { offerModel: model } = useOffreService();
    const filterOptions = useFilterOptions();

    const goToCreate = React.useCallback(() => navigate(offerCreatePath), [navigate]);
    const goToFiche = React.useCallback(
        (item: { id: string | number }) => navigate(offerFichePath(item.id)),
        [navigate],
    );

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer une nouvelle offre',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: goToCreate,
            },
        ],
        [goToCreate],
    );

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<OffresJsonldOffreRead>[] = [
        {
            label: 'Libellé',
            displayLabel: true,
            render: ({ iconeName, libelle }) => {
                if (iconeName === null) return null;

                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                        sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        <FaOptionIcon
                            size="lg"
                            icon={['fad', iconeName as IconName]}
                        />
                        {libelle}
                    </Typography>
                );
            },
            isDisplayed: true,
        },
        {
            label: 'Visibilité',
            displayLabel: true,
            render: ({ visibilite }) => {
                const isPartial = visibilite === 'PARTIAL';
                const icon = isPartial
                    ? visibilityPartialIcon.props.icon
                    : visibilityFullIcon.props.icon;
                const text = visibilityTranslation[visibilite as 'FULL' | 'PARTIAL'];

                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                        sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        <FaOptionIcon
                            icon={icon}
                            tooltip={text}
                        />
                        {text}
                    </Typography>
                );
            },
            isDisplayed: true,
            field: 'visibilite',
        },

        displayCustomValue<OffresJsonldOffreRead>(
            'domaine',
            (domaine: OffresJsonldOffreRead) => domaine?.libelle ?? '---',
            'Domaine',
        ),
    ];

    const filters: FiltersDatatableList = [
        getStaticSelectFilter('Domaine', 'domaineId', filterOptions.domains),
    ];

    return (
        <RoutedListing
            listingTitle="Liste des offres"
            createButton={{
                label: 'Créer une nouvelle offre',
                icon: addItem,
                onClick: goToCreate,
            }}
            listingColumns={columns as ColumnDatatable<unknown>[]}
            model={model}
            filters={filters}
            filtersControl
            searchBarType={PossibleTypes.Offer}
            onRowClick={goToFiche}
        />
    );
};

export default Panel;
